import type { ShadowDiffEntry } from "./types";

const EMPTY_IGNORED_KEYS: ReadonlySet<string> = new Set();

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Deep-compares two parsed websocket message values and returns one entry per JSON
 * path whose value differs between them, each carrying the GAIA value and the Simplr
 * value at that path. The raw values are kept so a redacted before/after can be logged
 * for triage — callers MUST scrub them (see `redactDiffs`) before emitting anywhere
 * that could contain customer data (PII).
 *
 * Keys listed in `ignoredKeys` are skipped at every depth (e.g. volatile server
 * timestamps that are expected to differ between the two backends).
 */
export function diffMessages(
    gaiaValue: unknown,
    simplrValue: unknown,
    ignoredKeys: ReadonlySet<string> = EMPTY_IGNORED_KEYS,
    path = "",
): ShadowDiffEntry[] {
    if (gaiaValue === simplrValue) return [];

    if (isPlainRecord(gaiaValue) && isPlainRecord(simplrValue)) {
        return diffRecords(gaiaValue, simplrValue, ignoredKeys, path);
    }

    if (Array.isArray(gaiaValue) && Array.isArray(simplrValue)) {
        return diffArrays(gaiaValue, simplrValue, ignoredKeys, path);
    }

    // Mismatched shapes (object vs array vs primitive) or differing primitives.
    return [{ path: path || "(root)", gaiaValue, simplrValue }];
}

function diffRecords(
    gaiaValue: Record<string, unknown>,
    simplrValue: Record<string, unknown>,
    ignoredKeys: ReadonlySet<string>,
    path: string,
): ShadowDiffEntry[] {
    const diffs: ShadowDiffEntry[] = [];
    const keys = new Set([...Object.keys(gaiaValue), ...Object.keys(simplrValue)]);

    for (const key of keys) {
        if (ignoredKeys.has(key)) continue;

        const childPath = path ? `${path}.${key}` : key;
        const inGaia = key in gaiaValue;
        const inSimplr = key in simplrValue;

        if (!inGaia || !inSimplr) {
            diffs.push({ path: childPath, gaiaValue: gaiaValue[key], simplrValue: simplrValue[key] });
            continue;
        }

        diffs.push(...diffMessages(gaiaValue[key], simplrValue[key], ignoredKeys, childPath));
    }

    return diffs;
}

function diffArrays(
    gaiaValue: unknown[],
    simplrValue: unknown[],
    ignoredKeys: ReadonlySet<string>,
    path: string,
): ShadowDiffEntry[] {
    const diffs: ShadowDiffEntry[] = [];
    const length = Math.max(gaiaValue.length, simplrValue.length);

    for (let index = 0; index < length; index++) {
        const childPath = `${path}[${index}]`;

        if (index >= gaiaValue.length || index >= simplrValue.length) {
            diffs.push({ path: childPath, gaiaValue: gaiaValue[index], simplrValue: simplrValue[index] });
            continue;
        }

        diffs.push(...diffMessages(gaiaValue[index], simplrValue[index], ignoredKeys, childPath));
    }

    return diffs;
}
